import React, { useState } from "react";
import { AdminSideMenu } from "../admin/AdminMenu";
import API_URL from "../../api/API";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";


// ============================================================
// TIMEZONE
// ============================================================

const TIMEZONE = "America/Chicago";


// ============================================================
// AUTOMATIC BREAK SETTINGS
// ============================================================

const BREAK_MINUTES = 30;

// Employee must work MORE than 6.75 hours.
const BREAK_THRESHOLD_MINUTES = 6.75 * 60;

// Maximum automatic break deduction per Monday-Sunday week.
const MAX_WEEKLY_BREAK_MINUTES = 150;


// ============================================================
// OVERTIME SETTINGS
// ============================================================

const REGULAR_WEEKLY_HOURS = 40;

const REGULAR_WEEKLY_MINUTES =
  REGULAR_WEEKLY_HOURS * 60;

const OVERTIME_MULTIPLIER = 1.5;


// ============================================================
// FORMAT DATE
// ============================================================

const formatDate = (dateValue) => {

  if (!dateValue) {
    return "-";
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      timeZone: TIMEZONE,
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }
  ).format(date);

};


// ============================================================
// FORMAT TIME
// ============================================================

const formatTime = (dateValue) => {

  if (!dateValue) {
    return "-";
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      timeZone: TIMEZONE,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }
  ).format(date);

};


// ============================================================
// FORMAT DAY
// ============================================================

const formatDay = (dateValue) => {

  if (!dateValue) {
    return "-";
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      timeZone: TIMEZONE,
      weekday: "long",
    }
  ).format(date);

};


// ============================================================
// FORMAT DATE WITH DAY
// ============================================================

const formatDateWithDay = (dateValue) => {

  if (!dateValue) {
    return "-";
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      timeZone: TIMEZONE,
      weekday: "long",
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }
  ).format(date);

};


// ============================================================
// GET DATE KEY
// ============================================================

const getDateKey = (dateValue) => {

  if (!dateValue) {
    return "";
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const parts =
    new Intl.DateTimeFormat(
      "en-US",
      {
        timeZone: TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }
    ).formatToParts(date);


  const year =
    parts.find(
      (part) =>
        part.type === "year"
    )?.value;


  const month =
    parts.find(
      (part) =>
        part.type === "month"
    )?.value;


  const day =
    parts.find(
      (part) =>
        part.type === "day"
    )?.value;


  return `${year}-${month}-${day}`;

};


// ============================================================
// FORMAT DATE KEY
// ============================================================

const formatDateKey = (dateKey) => {

  if (!dateKey) {
    return "-";
  }

  const [
    year,
    month,
    day,
  ] =
    dateKey.split("-");


  if (
    !year ||
    !month ||
    !day
  ) {
    return dateKey;
  }


  return `${month}/${day}/${year}`;

};


// ============================================================
// FORMAT DATE KEY WITH DAY
// ============================================================

const formatDateKeyWithDay = (dateKey) => {

  if (!dateKey) {
    return "-";
  }

  const [
    year,
    month,
    day,
  ] =
    dateKey
      .split("-")
      .map(Number);


  const date =
    new Date(
      Date.UTC(
        year,
        month - 1,
        day
      )
    );


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return dateKey;
  }


  const dayName =
    new Intl.DateTimeFormat(
      "en-US",
      {
        weekday: "long",
        timeZone: "UTC",
      }
    ).format(date);


  return `${dayName}, ${month
    .toString()
    .padStart(2, "0")}/${day
    .toString()
    .padStart(2, "0")}/${year}`;

};


// ============================================================
// GET CHICAGO DATE PARTS
// ============================================================

const getChicagoDateParts = (
  dateValue
) => {

  if (!dateValue) {
    return null;
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }


  const parts =
    new Intl.DateTimeFormat(
      "en-US",
      {
        timeZone: TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        weekday: "short",
      }
    ).formatToParts(date);


  const getPart =
    (type) =>
      parts.find(
        (part) =>
          part.type === type
      )?.value;


  return {
    year: getPart("year"),
    month: getPart("month"),
    day: getPart("day"),
    weekday: getPart("weekday"),
  };

};


// ============================================================
// IS SUNDAY
// ============================================================

const isSunday = (
  dateValue
) => {

  const parts =
    getChicagoDateParts(
      dateValue
    );

  if (!parts) {
    return false;
  }

  return (
    parts.weekday === "Sun"
  );

};


// ============================================================
// CALCULATE MINUTES
// ============================================================

const calculateMinutes = (
  clockIn,
  clockOut
) => {

  if (
    !clockIn ||
    !clockOut
  ) {
    return 0;
  }


  const start =
    new Date(clockIn);

  const end =
    new Date(clockOut);


  if (
    Number.isNaN(
      start.getTime()
    ) ||
    Number.isNaN(
      end.getTime()
    )
  ) {
    return 0;
  }


  const difference =
    end.getTime() -
    start.getTime();


  if (difference <= 0) {
    return 0;
  }


  return (
    difference /
    (1000 * 60)
  );

};


// ============================================================
// MINUTES TO HOURS
// ============================================================

const minutesToHours = (
  minutes
) => {

  return (
    minutes / 60
  ).toFixed(2);

};


// ============================================================
// FORMAT CURRENCY
// ============================================================

const formatCurrency = (
  amount
) => {

  const safeAmount =
    Number(amount) || 0;


  return new Intl.NumberFormat(
    "en-US",
    {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  ).format(
    safeAmount
  );

};


// ============================================================
// EMPLOYEE NAME
// ============================================================

const getEmployeeName = (
  employee
) => {

  if (!employee) {
    return "Unknown Employee";
  }


  const firstName =
    employee.firstName || "";

  const lastName =
    employee.lastName || "";


  const fullName =
    `${firstName} ${lastName}`.trim();


  return (
    fullName ||
    "Unknown Employee"
  );

};


// ============================================================
// STORE NAME
// ============================================================

const getStoreName = (
  store
) => {

  if (!store) {
    return "-";
  }


  if (
    typeof store ===
    "string"
  ) {
    return store;
  }


  return (
    store.name ||
    store.storeName ||
    "-"
  );

};


// ============================================================
// GET TIME ENTRY LOG
// ============================================================

const getLogForEntry = (
  logs,
  entryId
) => {

  if (
    !Array.isArray(logs) ||
    !entryId
  ) {
    return null;
  }


  return (
    logs.find(
      (log) =>
        String(log?.entryId) ===
        String(entryId)
    ) || null
  );

};


// ============================================================
// GET WEEK KEY
// ============================================================

const getWeekKey = (
  dateKey
) => {

  if (!dateKey) {
    return "";
  }


  const [
    year,
    month,
    day,
  ] =
    dateKey
      .split("-")
      .map(Number);


  const date =
    new Date(
      Date.UTC(
        year,
        month - 1,
        day
      )
    );


  const dayOfWeek =
    date.getUTCDay();


  const daysFromMonday =
    dayOfWeek === 0
      ? 6
      : dayOfWeek - 1;


  date.setUTCDate(
    date.getUTCDate() -
      daysFromMonday
  );


  return date
    .toISOString()
    .slice(0, 10);

};


// ============================================================
// ADD DAYS TO DATE KEY
// ============================================================

const addDaysToDateKey = (
  dateKey,
  days
) => {

  if (!dateKey) {
    return "";
  }


  const [
    year,
    month,
    day,
  ] =
    dateKey
      .split("-")
      .map(Number);


  const date =
    new Date(
      Date.UTC(
        year,
        month - 1,
        day
      )
    );


  date.setUTCDate(
    date.getUTCDate() +
      days
  );


  return date
    .toISOString()
    .slice(0, 10);

};


// ============================================================
// FORMAT WEEK RANGE
// ============================================================

const formatWeekRange = (
  weekKey
) => {

  if (!weekKey) {
    return "-";
  }


  const weekEnd =
    addDaysToDateKey(
      weekKey,
      6
    );


  return (
    `${formatDateKey(
      weekKey
    )} - ${formatDateKey(
      weekEnd
    )}`
  );

};


// ============================================================
// CALCULATE DAILY ELIGIBLE BREAK
// ============================================================

const calculateDailyBreakMinutes = (
  dateKey,
  dayEntries
) => {

  if (
    !dateKey ||
    !Array.isArray(
      dayEntries
    ) ||
    dayEntries.length === 0
  ) {
    return 0;
  }


  const firstEntry =
    dayEntries.find(
      (entry) =>
        entry?.clockIn
    );


  if (!firstEntry) {
    return 0;
  }


  if (
    isSunday(
      firstEntry.clockIn
    )
  ) {
    return 0;
  }


  const rawMinutes =
    dayEntries.reduce(
      (
        total,
        entry
      ) => {

        return (
          total +
          calculateMinutes(
            entry.clockIn,
            entry.clockOut
          )
        );

      },
      0
    );


  if (
    rawMinutes <=
    BREAK_THRESHOLD_MINUTES
  ) {
    return 0;
  }


  if (
    dayEntries.length > 1
  ) {
    return 0;
  }


  return BREAK_MINUTES;

};


// ============================================================
// CALCULATE EMPLOYEE DAILY TOTALS
// ============================================================

const calculateEmployeeDailyTotals = (
  dateKeys,
  groupedEntries
) => {

  const dailyTotals = {};

  const weeklyBreakMinutes = {};


  dateKeys.forEach(
    (dateKey) => {

      const dayEntries =
        groupedEntries[
          dateKey
        ] || [];


      const rawMinutes =
        dayEntries.reduce(
          (
            total,
            entry
          ) => {

            return (
              total +
              calculateMinutes(
                entry.clockIn,
                entry.clockOut
              )
            );

          },
          0
        );


      const eligibleBreakMinutes =
        calculateDailyBreakMinutes(
          dateKey,
          dayEntries
        );


      const weekKey =
        getWeekKey(
          dateKey
        );


      if (
        weeklyBreakMinutes[
          weekKey
        ] === undefined
      ) {

        weeklyBreakMinutes[
          weekKey
        ] = 0;

      }


      const remainingWeeklyBreak =
        Math.max(
          0,
          MAX_WEEKLY_BREAK_MINUTES -
            weeklyBreakMinutes[
              weekKey
            ]
        );


      const actualBreakMinutes =
        Math.min(
          eligibleBreakMinutes,
          remainingWeeklyBreak
        );


      weeklyBreakMinutes[
        weekKey
      ] += actualBreakMinutes;


      const countedMinutes =
        Math.max(
          0,
          rawMinutes -
            actualBreakMinutes
        );


      dailyTotals[
        dateKey
      ] = {

        rawMinutes,

        eligibleBreakMinutes,

        actualBreakMinutes,

        countedMinutes,

        weekKey,

      };

    }
  );


  return {

    dailyTotals,

    weeklyBreakMinutes,

  };

};


// ============================================================
// CALCULATE WEEKLY PAY
// ============================================================

const calculateWeeklyTotals = (
  dateKeys,
  dailyTotals,
  hourlyRate
) => {

  const weeklyTotals = {};


  dateKeys.forEach(
    (dateKey) => {

      const dayTotals =
        dailyTotals[
          dateKey
        ];


      if (!dayTotals) {
        return;
      }


      const weekKey =
        dayTotals.weekKey;


      if (
        !weeklyTotals[
          weekKey
        ]
      ) {

        weeklyTotals[
          weekKey
        ] = {

          weekKey,

          rawMinutes: 0,

          breakMinutes: 0,

          countedMinutes: 0,

          regularMinutes: 0,

          overtimeMinutes: 0,

          regularPay: 0,

          overtimePay: 0,

          totalPay: 0,

        };

      }


      weeklyTotals[
        weekKey
      ].rawMinutes +=
        dayTotals.rawMinutes;


      weeklyTotals[
        weekKey
      ].breakMinutes +=
        dayTotals.actualBreakMinutes;


      weeklyTotals[
        weekKey
      ].countedMinutes +=
        dayTotals.countedMinutes;

    }
  );


  Object.values(
    weeklyTotals
  ).forEach(
    (week) => {

      week.regularMinutes =
        Math.min(
          week.countedMinutes,
          REGULAR_WEEKLY_MINUTES
        );


      week.overtimeMinutes =
        Math.max(
          0,
          week.countedMinutes -
            REGULAR_WEEKLY_MINUTES
        );


      const regularHours =
        week.regularMinutes /
        60;


      const overtimeHours =
        week.overtimeMinutes /
        60;


      week.regularPay =
        regularHours *
        hourlyRate;


      week.overtimePay =
        overtimeHours *
        hourlyRate *
        OVERTIME_MULTIPLIER;


      week.totalPay =
        week.regularPay +
        week.overtimePay;

    }
  );


  return weeklyTotals;

};


// ============================================================
// MAIN COMPONENT
// ============================================================

export const AdminTimeReportManage = () => {

  const {
    admin
  } =
    useAdminAuthContext();


  // ==========================================================
  // STATE
  // ==========================================================

  const [
    startDate,
    setStartDate
  ] =
    useState("");


  const [
    endDate,
    setEndDate
  ] =
    useState("");


  const [
    report,
    setReport
  ] =
    useState([]);


  const [
    isLoading,
    setIsLoading
  ] =
    useState(false);


  const [
    error,
    setError
  ] =
    useState("");


  const [
    expandedEmployees,
    setExpandedEmployees
  ] =
    useState({});


  const [
    expandedLogs,
    setExpandedLogs
  ] =
    useState({});


  // ==========================================================
  // FETCH REPORT
  // ==========================================================

  const fetchTimeReport =
    async () => {

      try {

        setError("");


        // ======================================================
        // BASIC DATE VALIDATION
        // ======================================================

        if (
          !startDate ||
          !endDate
        ) {

          setError(
            "Please select a start date and end date."
          );

          return;

        }


        // ======================================================
        // START DATE MUST BE MONDAY
        // ======================================================

        const startDay =
          new Date(
            `${startDate}T00:00:00`
          ).getDay();


        if (
          startDay !== 1
        ) {

          setError(
            "Start date must be a Monday."
          );

          return;

        }


        // ======================================================
        // END DATE MUST BE SUNDAY
        // ======================================================

        const endDay =
          new Date(
            `${endDate}T00:00:00`
          ).getDay();


        if (
          endDay !== 0
        ) {

          setError(
            "End date must be a Sunday."
          );

          return;

        }


        // ======================================================
        // START CANNOT BE AFTER END
        // ======================================================

        if (
          startDate >
          endDate
        ) {

          setError(
            "Start date cannot be after end date."
          );

          return;

        }


        // ======================================================
        // ADMIN AUTHENTICATION
        // ======================================================

        if (
          !admin?.token
        ) {

          setError(
            "You are not authorized. Please log in again."
          );

          return;

        }


        setIsLoading(
          true
        );


        // ======================================================
        // API REQUEST
        // ======================================================

        const response =
          await fetch(

            API_URL.ADMIN_TIMEREPORT_GET +
              `?startDate=${encodeURIComponent(
                startDate
              )}&endDate=${encodeURIComponent(
                endDate
              )}`,

            {

              method: "GET",

              headers: {

                Authorization:
                  `Bearer ${admin.token}`,

              },

            }

          );


        const data =
          await response.json();


        console.log(
          "TIME REPORT RESPONSE:",
          data
        );


        if (
          !response.ok
        ) {

          throw new Error(
            data.error ||
            data.message ||
            "Failed to generate time report"
          );

        }


        const reportData =
          Array.isArray(
            data.report
          )
            ? data.report
            : [];


        setExpandedEmployees(
          {}
        );


        setExpandedLogs(
          {}
        );


        setReport(
          reportData
        );


      } catch (
        err
      ) {

        console.error(
          "TIME REPORT ERROR:",
          err
        );


        setReport([]);


        setExpandedEmployees(
          {}
        );


        setExpandedLogs(
          {}
        );


        setError(
          err.message ||
          "Failed to generate time report"
        );


      } finally {

        setIsLoading(
          false
        );

      }

    };


  // ==========================================================
  // TOGGLE EMPLOYEE
  // ==========================================================

  const toggleEmployee = (
    employeeId
  ) => {

    setExpandedEmployees(
      (
        previous
      ) => {

        const currentlyExpanded =
          previous[
            employeeId
          ] === true;


        return {

          ...previous,

          [employeeId]:
            !currentlyExpanded,

        };

      }
    );

  };


  // ==========================================================
  // TOGGLE TIME ENTRY DETAILS
  // ==========================================================

  const toggleLog = (
    entryId
  ) => {

    if (!entryId) {
      return;
    }


    setExpandedLogs(
      (
        previous
      ) => {

        const currentlyExpanded =
          previous[
            entryId
          ] === true;


        return {

          ...previous,

          [entryId]:
            !currentlyExpanded,

        };

      }
    );

  };


  // ==========================================================
  // GROUP ENTRIES BY DATE
  // ==========================================================

  const groupEntriesByDate = (
    entries
  ) => {

    const grouped = {};


    if (
      !Array.isArray(
        entries
      )
    ) {
      return grouped;
    }


    entries.forEach(
      (
        entry
      ) => {

        const dateKey =
          getDateKey(
            entry.clockIn
          );


        if (!dateKey) {
          return;
        }


        if (
          !grouped[
            dateKey
          ]
        ) {

          grouped[
            dateKey
          ] = [];

        }


        grouped[
          dateKey
        ].push(
          entry
        );

      }
    );


    return grouped;

  };


  // ==========================================================
  // SORT REPORT
  // ==========================================================

  const sortedReport =
    [...report].sort(
      (
        a,
        b
      ) => {

        const nameA =
          getEmployeeName(
            a?.employee
          ).toLowerCase();


        const nameB =
          getEmployeeName(
            b?.employee
          ).toLowerCase();


        return nameA.localeCompare(
          nameB
        );

      }
    );


  // ==========================================================
  // RENDER
  // ==========================================================

  return (

    <div className="flex h-screen">


      {/* ======================================================
          LEFT SIDE MENU
      ====================================================== */}

      <AdminSideMenu />


      {/* ======================================================
          MAIN CONTENT
      ====================================================== */}

      <div className="flex-1 p-6 bg-gray-100 pt-16 flex flex-col overflow-hidden">


        {/* ====================================================
            HEADER
        ==================================================== */}

        <div className="flex justify-between items-center mb-4">

          <div>

            <h1 className="text-2xl font-bold">

              Time Report

            </h1>

            <p className="text-sm text-gray-500 mt-1">

              Employee hours, overtime, and payroll report

            </p>

          </div>

        </div>


        {/* ====================================================
            FILTER SECTION
        ==================================================== */}

        <div className="bg-white border rounded shadow p-4 mb-4">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">


            {/* START DATE */}

            <div>

              <label className="block text-sm font-medium mb-1">

                Start Date

              </label>

              <input
                type="date"
                value={startDate}
                onChange={
                  (e) =>
                    setStartDate(
                      e.target.value
                    )
                }
                className="w-full p-2 border border-gray-300 rounded"
              />

            </div>


            {/* END DATE */}

            <div>

              <label className="block text-sm font-medium mb-1">

                End Date

              </label>

              <input
                type="date"
                value={endDate}
                onChange={
                  (e) =>
                    setEndDate(
                      e.target.value
                    )
                  }
                className="w-full p-2 border border-gray-300 rounded"
              />

            </div>


            {/* BUTTON */}

            <div className="flex items-end">

              <button
                type="button"
                onClick={
                  fetchTimeReport
                }
                disabled={
                  isLoading
                }
                className="w-full px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-600 disabled:opacity-50"
              >

                {
                  isLoading
                    ? "Loading..."
                    : "Generate Report"
                }

              </button>

            </div>

          </div>


          {/* ERROR */}

          {error && (

            <div className="mt-4 p-3 bg-red-100 text-red-600 rounded">

              {error}

            </div>

          )}

        </div>


        {/* ====================================================
            REPORT AREA
        ==================================================== */}

        <div className="flex-1 overflow-auto">


          {/* LOADING */}

          {isLoading && (

            <div className="bg-white border rounded shadow p-8 text-center">

              <p className="text-blue-600">

                Loading time report...

              </p>

            </div>

          )}


          {/* NO RESULTS */}

          {!isLoading &&
            report.length === 0 &&
            !error && (

              <div className="bg-white border rounded shadow p-8 text-center">

                <p className="text-gray-500">

                  Select a date range and generate the report.

                </p>

              </div>

            )}


          {/* REPORT */}

          {!isLoading &&
            sortedReport.length > 0 && (

              <div className="space-y-4">


                {sortedReport.map(
                  (
                    employeeReport,
                    employeeIndex
                  ) => {


                    const employee =
                      employeeReport?.employee;


                    const entries =
                      Array.isArray(
                        employeeReport?.entries
                      )
                        ? employeeReport.entries
                        : [];


                    const logs =
                      Array.isArray(
                        employeeReport?.logs
                      )
                        ? employeeReport.logs
                        : [];


                    const employeeId =
                      employee?._id ||
                      `employee-${employeeIndex}`;


                    const employeeName =
                      getEmployeeName(
                        employee
                      );


                    const hourlyRate =
                      entries.length > 0
                        ? Number(
                            entries[0].hourlyRate
                          ) || 0
                        : 0;


                    const groupedEntries =
                      groupEntriesByDate(
                        entries
                      );


                    const dateKeys =
                      Object.keys(
                        groupedEntries
                      ).sort();


                    const {
                      dailyTotals,
                    } =
                      calculateEmployeeDailyTotals(
                        dateKeys,
                        groupedEntries
                      );


                    const weeklyTotals =
                      calculateWeeklyTotals(
                        dateKeys,
                        dailyTotals,
                        hourlyRate
                      );


                    const weekKeys =
                      Object.keys(
                        weeklyTotals
                      ).sort();


                    const employeeTotals =
                      weekKeys.reduce(
                        (
                          totals,
                          weekKey
                        ) => {

                          const week =
                            weeklyTotals[
                              weekKey
                            ];


                          totals.rawMinutes +=
                            week.rawMinutes;


                          totals.breakMinutes +=
                            week.breakMinutes;


                          totals.countedMinutes +=
                            week.countedMinutes;


                          totals.regularMinutes +=
                            week.regularMinutes;


                          totals.overtimeMinutes +=
                            week.overtimeMinutes;


                          totals.regularPay +=
                            week.regularPay;


                          totals.overtimePay +=
                            week.overtimePay;


                          totals.totalPay +=
                            week.totalPay;


                          return totals;

                        },

                        {

                          rawMinutes: 0,

                          breakMinutes: 0,

                          countedMinutes: 0,

                          regularMinutes: 0,

                          overtimeMinutes: 0,

                          regularPay: 0,

                          overtimePay: 0,

                          totalPay: 0,

                        }

                      );


                    const employeeRawHours =
                      minutesToHours(
                        employeeTotals.rawMinutes
                      );


                    const employeeBreakHours =
                      minutesToHours(
                        employeeTotals.breakMinutes
                      );


                    const employeeCountedHours =
                      minutesToHours(
                        employeeTotals.countedMinutes
                      );


                    const employeeRegularHours =
                      minutesToHours(
                        employeeTotals.regularMinutes
                      );


                    const employeeOvertimeHours =
                      minutesToHours(
                        employeeTotals.overtimeMinutes
                      );


                    const isExpanded =
                      expandedEmployees[
                        employeeId
                      ] === true;


                    return (

                      <div
                        key={employeeId}
                        className="bg-white border rounded shadow overflow-hidden"
                      >


                        {/* EMPLOYEE HEADER */}

                        <button
                          type="button"
                          onClick={
                            () =>
                              toggleEmployee(
                                employeeId
                              )
                          }
                          className="w-full px-5 py-4 bg-white hover:bg-gray-50 border-b"
                        >

                          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-5">

                            <div className="text-left">

                              <h2 className="text-lg font-bold text-gray-800">

                                {employeeName}

                              </h2>

                              <p className="text-sm text-gray-500 mt-1">

                                Hourly Rate:{" "}

                                <span className="font-medium text-gray-700">

                                  {formatCurrency(
                                    hourlyRate
                                  )}

                                </span>

                              </p>

                              <p className="text-sm text-gray-500">

                                {entries.length}{" "}

                                {
                                  entries.length === 1
                                    ? "log"
                                    : "logs"
                                }

                              </p>

                            </div>


                            <div className="flex flex-wrap justify-end items-center gap-x-6 gap-y-4">


                              <div className="text-right">

                                <p className="text-xs text-gray-500">

                                  Raw Hours

                                </p>

                                <p className="text-sm font-medium">

                                  {employeeRawHours}

                                </p>

                              </div>


                              <div className="text-right">

                                <p className="text-xs text-gray-500">

                                  Break

                                </p>

                                <p className="text-sm font-medium">

                                  -{employeeBreakHours}

                                </p>

                              </div>


                              <div className="text-right">

                                <p className="text-xs text-gray-500">

                                  Regular Hours

                                </p>

                                <p className="text-sm font-medium">

                                  {employeeRegularHours}

                                </p>

                              </div>


                              <div className="text-right">

                                <p className="text-xs text-gray-500">

                                  OT Hours

                                </p>

                                <p className="text-sm font-medium">

                                  {employeeOvertimeHours}

                                </p>

                              </div>


                              <div className="text-right border-l pl-6">

                                <p className="text-xs text-gray-500 font-medium">

                                  TOTAL AMOUNT TO PAY

                                </p>

                                <p className="text-2xl font-bold text-green-700">

                                  {formatCurrency(
                                    employeeTotals.totalPay
                                  )}

                                </p>

                              </div>


                              <span className="text-gray-500 text-lg">

                                {
                                  isExpanded
                                    ? "▲"
                                    : "▼"
                                }

                              </span>

                            </div>

                          </div>

                        </button>


                        {/* EXPANDED CONTENT */}

                        {isExpanded && (

                          <div className="p-5">


                            {/* PAY SUMMARY */}

                            <div className="border rounded-lg p-5 mb-6 bg-gray-50">

                              <h3 className="font-bold text-gray-800 mb-4">

                                Payroll Summary

                              </h3>


                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">


                                <div>

                                  <p className="text-xs text-gray-500">

                                    Regular Pay

                                  </p>

                                  <p className="text-lg font-semibold">

                                    {formatCurrency(
                                      employeeTotals.regularPay
                                    )}

                                  </p>

                                </div>


                                <div>

                                  <p className="text-xs text-gray-500">

                                    Overtime Pay

                                  </p>

                                  <p className="text-lg font-semibold">

                                    {formatCurrency(
                                      employeeTotals.overtimePay
                                    )}

                                  </p>

                                </div>


                                <div>

                                  <p className="text-xs text-gray-500">

                                    Counted Hours

                                  </p>

                                  <p className="text-lg font-semibold">

                                    {employeeCountedHours}

                                  </p>

                                </div>


                                <div className="md:text-right">

                                  <p className="text-xs text-gray-500 font-medium">

                                    TOTAL AMOUNT TO PAY

                                  </p>

                                  <p className="text-2xl font-bold text-green-700">

                                    {formatCurrency(
                                      employeeTotals.totalPay
                                    )}

                                  </p>

                                </div>

                              </div>

                            </div>


                            {/* WEEKLY PAY BREAKDOWN */}

                            <h3 className="text-lg font-bold text-gray-800 mb-4">

                              Weekly Payroll Breakdown

                            </h3>


                            <div className="space-y-4 mb-8">


                              {weekKeys.map(
                                (
                                  weekKey
                                ) => {


                                  const week =
                                    weeklyTotals[
                                      weekKey
                                    ];


                                  const weekRawHours =
                                    minutesToHours(
                                      week.rawMinutes
                                    );


                                  const weekBreakHours =
                                    minutesToHours(
                                      week.breakMinutes
                                    );


                                  const weekCountedHours =
                                    minutesToHours(
                                      week.countedMinutes
                                    );


                                  const weekRegularHours =
                                    minutesToHours(
                                      week.regularMinutes
                                    );


                                  const weekOvertimeHours =
                                    minutesToHours(
                                      week.overtimeMinutes
                                    );


                                  return (

                                    <div
                                      key={weekKey}
                                      className="border rounded-lg overflow-hidden bg-white"
                                    >

                                      <div className="bg-gray-100 px-4 py-3 border-b">

                                        <h4 className="font-bold text-gray-800">

                                          Week:{" "}

                                          {formatWeekRange(
                                            weekKey
                                          )}

                                        </h4>

                                      </div>


                                      <div className="p-4">

                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">


                                          <div>

                                            <p className="text-xs text-gray-500">

                                              Raw Hours

                                            </p>

                                            <p className="font-semibold">

                                              {weekRawHours}

                                            </p>

                                          </div>


                                          <div>

                                            <p className="text-xs text-gray-500">

                                              Break

                                            </p>

                                            <p className="font-semibold">

                                              -{weekBreakHours}

                                            </p>

                                          </div>


                                          <div>

                                            <p className="text-xs text-gray-500">

                                              Counted Hours

                                            </p>

                                            <p className="font-semibold">

                                              {weekCountedHours}

                                            </p>

                                          </div>


                                          <div>

                                            <p className="text-xs text-gray-500">

                                              Regular Hours

                                            </p>

                                            <p className="font-semibold">

                                              {weekRegularHours}

                                            </p>

                                          </div>


                                          <div>

                                            <p className="text-xs text-gray-500">

                                              OT Hours

                                            </p>

                                            <p className="font-semibold">

                                              {weekOvertimeHours}

                                            </p>

                                          </div>


                                          <div>

                                            <p className="text-xs text-gray-500">

                                              Week Total

                                            </p>

                                            <p className="font-bold text-green-700 text-lg">

                                              {formatCurrency(
                                                week.totalPay
                                              )}

                                            </p>

                                          </div>

                                        </div>


                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t mt-4 pt-4">


                                          <div>

                                            <p className="text-xs text-gray-500">

                                              Regular Pay

                                            </p>

                                            <p className="font-semibold">

                                              {formatCurrency(
                                                week.regularPay
                                              )}

                                            </p>

                                          </div>


                                          <div>

                                            <p className="text-xs text-gray-500">

                                              OT Pay (1.5× Rate)

                                            </p>

                                            <p className="font-semibold">

                                              {formatCurrency(
                                                week.overtimePay
                                              )}

                                            </p>

                                          </div>


                                          <div className="md:text-right">

                                            <p className="text-xs text-gray-500">

                                              Hourly Rate

                                            </p>

                                            <p className="font-semibold">

                                              {formatCurrency(
                                                hourlyRate
                                              )}

                                            </p>

                                          </div>

                                        </div>

                                      </div>

                                    </div>

                                  );

                                }
                              )}

                            </div>


                            {/* DAILY DETAILS */}

                            <div className="flex items-center justify-between mb-4">

                              <div>

                                <h3 className="text-lg font-bold text-gray-800">

                                  Daily Time Logs

                                </h3>

                                <p className="text-sm text-gray-500 mt-1">

                                  Clock-in and clock-out records by day

                                </p>

                              </div>

                            </div>


                            <div className="space-y-6">


                              {dateKeys.map(
                                (
                                  dateKey
                                ) => {


                                  const dayEntries =
                                    groupedEntries[
                                      dateKey
                                    ];


                                  const dayTotals =
                                    dailyTotals[
                                      dateKey
                                    ];


                                  const dayRawHours =
                                    minutesToHours(
                                      dayTotals.rawMinutes
                                    );


                                  const dayBreakHours =
                                    minutesToHours(
                                      dayTotals.actualBreakMinutes
                                    );


                                  const dayCountedHours =
                                    minutesToHours(
                                      dayTotals.countedMinutes
                                    );


                                  const firstEntry =
                                    dayEntries.find(
                                      (entry) =>
                                        entry?.clockIn
                                    );


                                  const sunday =
                                    firstEntry
                                      ? isSunday(
                                          firstEntry.clockIn
                                        )
                                      : false;


                                  const hadClockOutBreak =
                                    dayEntries.length >
                                    1;


                                  const exceededBreakThreshold =
                                    dayTotals.rawMinutes >
                                    BREAK_THRESHOLD_MINUTES;


                                  const weeklyBreakWasCapped =
                                    dayTotals.eligibleBreakMinutes >
                                    dayTotals.actualBreakMinutes;


                                  return (

                                    <div
                                      key={dateKey}
                                      className="border rounded-lg overflow-hidden bg-white shadow-sm"
                                    >


                                      {/* DATE HEADER */}

                                      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 bg-gray-50 border-b px-5 py-4">


                                        <div>

                                          <h3 className="font-bold text-gray-800 text-base">

                                            {formatDateKeyWithDay(
                                              dateKey
                                            )}

                                          </h3>


                                          <p className="text-xs text-gray-500 mt-1">

                                            {
                                              sunday

                                                ? "Sunday — no automatic break deduction"

                                                : !exceededBreakThreshold

                                                  ? "Worked 6.75 hours or less — no automatic break deduction"

                                                  : hadClockOutBreak

                                                    ? "Employee clocked out during the day — no automatic break deduction"

                                                    : weeklyBreakWasCapped

                                                      ? "Weekly automatic break limit reached — no additional break deducted"

                                                      : "Automatic 30-minute break deducted"
                                            }

                                          </p>

                                        </div>


                                        <div className="flex items-center gap-8">


                                          <div className="text-right">

                                            <p className="text-xs text-gray-500">

                                              Raw

                                            </p>

                                            <p className="font-semibold text-gray-700">

                                              {dayRawHours}

                                            </p>

                                          </div>


                                          <div className="text-right">

                                            <p className="text-xs text-gray-500">

                                              Break

                                            </p>

                                            <p className="font-semibold text-gray-700">

                                              {
                                                dayTotals.actualBreakMinutes > 0
                                                  ? `-${dayBreakHours}`
                                                  : "0.00"
                                              }

                                            </p>

                                          </div>


                                          <div className="text-right">

                                            <p className="text-xs text-gray-500">

                                              Counted

                                            </p>

                                            <p className="font-bold text-gray-900">

                                              {dayCountedHours}

                                            </p>

                                          </div>

                                        </div>

                                      </div>


                                      {/* LOG TABLE */}

                                      <div className="overflow-x-auto">

                                        <table className="w-full text-sm">


                                          <thead className="bg-gray-100 border-b">

                                            <tr>

                                              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                #
                                              </th>

                                              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                Date
                                              </th>

                                              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                Day
                                              </th>

                                              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                Clock In
                                              </th>

                                              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                Clock Out
                                              </th>

                                              <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                Hours
                                              </th>

                                              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                Store
                                              </th>

                                              <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                Details
                                              </th>

                                            </tr>

                                          </thead>


                                          <tbody>

                                            {dayEntries.map(
                                              (
                                                entry,
                                                entryIndex
                                              ) => {


                                                const minutes =
                                                  calculateMinutes(
                                                    entry.clockIn,
                                                    entry.clockOut
                                                  );


                                                const hours =
                                                  minutesToHours(
                                                    minutes
                                                  );


                                                const log =
                                                  getLogForEntry(
                                                    logs,
                                                    entry?._id
                                                  );


                                                const logId =
                                                  entry?._id ||
                                                  `${dateKey}-${entryIndex}`;


                                                const isLogExpanded =
                                                  expandedLogs[
                                                    logId
                                                  ] === true;


                                                const clockInLocation =
                                                  log?.clockIn?.location;


                                                const clockOutLocation =
                                                  log?.clockOut?.location;


                                                return (

                                                  <React.Fragment
                                                    key={logId}
                                                  >

                                                    <tr
                                                      className="border-b hover:bg-gray-50"
                                                    >

                                                      <td className="px-5 py-3 text-gray-400 font-medium">

                                                        {entryIndex + 1}

                                                      </td>


                                                      <td className="px-5 py-3 whitespace-nowrap text-gray-700">

                                                        {formatDate(
                                                          entry.clockIn
                                                        )}

                                                      </td>


                                                      <td className="px-5 py-3 whitespace-nowrap font-medium text-gray-700">

                                                        {formatDay(
                                                          entry.clockIn
                                                        )}

                                                      </td>


                                                      <td className="px-5 py-3 whitespace-nowrap">

                                                        <span className="inline-flex items-center px-2.5 py-1 rounded bg-gray-100 text-gray-800 font-medium">

                                                          {formatTime(
                                                            entry.clockIn
                                                          )}

                                                        </span>

                                                      </td>


                                                      <td className="px-5 py-3 whitespace-nowrap">

                                                        <span className="inline-flex items-center px-2.5 py-1 rounded bg-gray-100 text-gray-800 font-medium">

                                                          {formatTime(
                                                            entry.clockOut
                                                          )}

                                                        </span>

                                                      </td>


                                                      <td className="px-5 py-3 text-right whitespace-nowrap">

                                                        <span className="font-semibold text-gray-800">

                                                          {hours}

                                                        </span>

                                                      </td>


                                                      <td className="px-5 py-3 whitespace-nowrap text-gray-700">

                                                        {getStoreName(
                                                          entry.store
                                                        )}

                                                      </td>


                                                      <td className="px-5 py-3 whitespace-nowrap text-right">

                                                        {log ? (

                                                          <button
                                                            type="button"
                                                            onClick={() =>
                                                              toggleLog(
                                                                logId
                                                              )
                                                            }
                                                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                                                          >

                                                            {
                                                              isLogExpanded
                                                                ? "Hide Details"
                                                                : "Details"
                                                            }

                                                            <span className="text-gray-400">

                                                              {
                                                                isLogExpanded
                                                                  ? "▲"
                                                                  : "▼"
                                                              }

                                                            </span>

                                                          </button>

                                                        ) : (

                                                          <span className="text-xs text-gray-400">

                                                            No details

                                                          </span>

                                                        )}

                                                      </td>

                                                    </tr>


                                                    {isLogExpanded &&
                                                      log && (

                                                        <tr>

                                                          <td
                                                            colSpan="8"
                                                            className="bg-gray-50 border-b"
                                                          >

                                                            <div className="p-5">

                                                              <div className="border rounded-lg bg-white shadow-sm overflow-hidden">


                                                                <div className="px-5 py-4 border-b bg-gray-100">

                                                                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">

                                                                    <div>

                                                                      <h4 className="font-bold text-gray-800">

                                                                        Time Entry Details

                                                                      </h4>

                                                                      <p className="text-xs text-gray-500 mt-1">

                                                                        Detailed clock-in and clock-out audit information

                                                                      </p>

                                                                    </div>


                                                                    <span
                                                                      className={`inline-flex w-fit items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                                                        log.isClockedOut
                                                                          ? "bg-green-100 text-green-700"
                                                                          : "bg-yellow-100 text-yellow-700"
                                                                      }`}
                                                                    >

                                                                      {
                                                                        log.isClockedOut
                                                                          ? "Clocked Out"
                                                                          : "Still Clocked In"
                                                                      }

                                                                    </span>

                                                                  </div>

                                                                </div>


                                                                <div className="p-5">

                                                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">


                                                                    <div>

                                                                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                                        Entry ID
                                                                      </p>

                                                                      <p className="text-sm font-medium text-gray-800 mt-1 break-all">

                                                                        {
                                                                          log.entryId
                                                                            ? String(
                                                                                log.entryId
                                                                              )
                                                                            : "-"
                                                                        }

                                                                      </p>

                                                                    </div>


                                                                    <div>

                                                                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                                        Store
                                                                      </p>

                                                                      <p className="text-sm font-medium text-gray-800 mt-1">

                                                                        {
                                                                          log.store?.name ||
                                                                          "-"
                                                                        }

                                                                      </p>

                                                                    </div>


                                                                    <div>

                                                                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                                        Store Selection
                                                                      </p>

                                                                      <p className="text-sm font-medium text-gray-800 mt-1">

                                                                        {
                                                                          log.storeSelectionLabel ||
                                                                          "-"
                                                                        }

                                                                      </p>

                                                                    </div>


                                                                    <div>

                                                                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                                        Hourly Rate
                                                                      </p>

                                                                      <p className="text-sm font-bold text-gray-800 mt-1">

                                                                        {
                                                                          log.hourlyRate !== null &&
                                                                          log.hourlyRate !== undefined
                                                                            ? formatCurrency(
                                                                                log.hourlyRate
                                                                              )
                                                                            : "-"
                                                                        }

                                                                      </p>

                                                                    </div>

                                                                  </div>


                                                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">


                                                                    {/* CLOCK IN */}

                                                                    <div className="border rounded-lg overflow-hidden">

                                                                      <div className="px-4 py-3 bg-gray-100 border-b">

                                                                        <h5 className="font-bold text-gray-800">
                                                                          Clock In
                                                                        </h5>

                                                                      </div>


                                                                      <div className="p-4 space-y-4">


                                                                        <div>

                                                                          <p className="text-xs text-gray-500">
                                                                            Time
                                                                          </p>

                                                                          <p className="text-sm font-semibold text-gray-800 mt-1">

                                                                            {
                                                                              formatDateWithDay(
                                                                                log.clockIn?.time
                                                                              )
                                                                            }

                                                                            <span className="mx-2 text-gray-400">
                                                                              •
                                                                            </span>

                                                                            {
                                                                              formatTime(
                                                                                log.clockIn?.time
                                                                              )
                                                                            }

                                                                          </p>

                                                                        </div>


                                                                        <div>

                                                                          <p className="text-xs text-gray-500">
                                                                            GPS Location
                                                                          </p>

                                                                          <p className="text-sm font-medium text-gray-800 mt-1 break-all">

                                                                            {
                                                                              clockInLocation?.latitude !== null &&
                                                                              clockInLocation?.latitude !== undefined &&
                                                                              clockInLocation?.longitude !== null &&
                                                                              clockInLocation?.longitude !== undefined

                                                                                ? `${clockInLocation.latitude}, ${clockInLocation.longitude}`

                                                                                : "-"
                                                                            }

                                                                          </p>

                                                                        </div>


                                                                        <div>

                                                                          <p className="text-xs text-gray-500">
                                                                            Distance from Store
                                                                          </p>

                                                                          <p className="text-sm font-medium text-gray-800 mt-1">

                                                                            {
                                                                              log.clockIn?.distanceFromStore !== null &&
                                                                              log.clockIn?.distanceFromStore !== undefined

                                                                                ? `${log.clockIn.distanceFromStore} meters`

                                                                                : "-"
                                                                            }

                                                                          </p>

                                                                        </div>


                                                                        <div>

                                                                          <p className="text-xs text-gray-500">
                                                                            Geofence Radius
                                                                          </p>

                                                                          <p className="text-sm font-medium text-gray-800 mt-1">

                                                                            {
                                                                              log.clockIn?.geofenceRadius !== null &&
                                                                              log.clockIn?.geofenceRadius !== undefined

                                                                                ? `${log.clockIn.geofenceRadius} meters`

                                                                                : "-"
                                                                            }

                                                                          </p>

                                                                        </div>

                                                                      </div>

                                                                    </div>


                                                                    {/* CLOCK OUT */}

                                                                    <div className="border rounded-lg overflow-hidden">

                                                                      <div className="px-4 py-3 bg-gray-100 border-b">

                                                                        <h5 className="font-bold text-gray-800">
                                                                          Clock Out
                                                                        </h5>

                                                                      </div>


                                                                      <div className="p-4 space-y-4">


                                                                        <div>

                                                                          <p className="text-xs text-gray-500">
                                                                            Time
                                                                          </p>

                                                                          <p className="text-sm font-semibold text-gray-800 mt-1">

                                                                            {
                                                                              log.clockOut?.time
                                                                                ? (

                                                                                  <>

                                                                                    {
                                                                                      formatDateWithDay(
                                                                                        log.clockOut.time
                                                                                      )
                                                                                    }

                                                                                    <span className="mx-2 text-gray-400">
                                                                                      •
                                                                                    </span>

                                                                                    {
                                                                                      formatTime(
                                                                                        log.clockOut.time
                                                                                      )
                                                                                    }

                                                                                  </>

                                                                                )
                                                                                : "-"
                                                                            }

                                                                          </p>

                                                                        </div>


                                                                        <div>

                                                                          <p className="text-xs text-gray-500">
                                                                            GPS Location
                                                                          </p>

                                                                          <p className="text-sm font-medium text-gray-800 mt-1 break-all">

                                                                            {
                                                                              clockOutLocation?.latitude !== null &&
                                                                              clockOutLocation?.latitude !== undefined &&
                                                                              clockOutLocation?.longitude !== null &&
                                                                              clockOutLocation?.longitude !== undefined

                                                                                ? `${clockOutLocation.latitude}, ${clockOutLocation.longitude}`

                                                                                : "-"
                                                                            }

                                                                          </p>

                                                                        </div>


                                                                        <div>

                                                                          <p className="text-xs text-gray-500">
                                                                            Distance from Store
                                                                          </p>

                                                                          <p className="text-sm font-medium text-gray-800 mt-1">

                                                                            {
                                                                              log.clockOut?.distanceFromStore !== null &&
                                                                              log.clockOut?.distanceFromStore !== undefined

                                                                                ? `${log.clockOut.distanceFromStore} meters`

                                                                                : "-"
                                                                            }

                                                                          </p>

                                                                        </div>


                                                                        <div>

                                                                          <p className="text-xs text-gray-500">
                                                                            Status
                                                                          </p>

                                                                          <p className="text-sm font-medium text-gray-800 mt-1">

                                                                            {
                                                                              log.isClockedOut
                                                                                ? "Clocked Out"
                                                                                : "Still Clocked In"
                                                                            }

                                                                          </p>

                                                                        </div>

                                                                      </div>

                                                                    </div>

                                                                  </div>

                                                                </div>

                                                              </div>

                                                            </div>

                                                          </td>

                                                        </tr>

                                                      )}

                                                  </React.Fragment>

                                                );

                                              }
                                            )}

                                          </tbody>

                                        </table>

                                      </div>

                                    </div>

                                  );

                                }
                              )}

                            </div>


                            {/* FINAL EMPLOYEE PAYROLL TOTAL */}

                            <div className="border-t-2 mt-8 pt-6">

                              <div className="max-w-md ml-auto">

                                <h3 className="font-bold text-gray-800 mb-4">
                                  Final Employee Payroll
                                </h3>


                                <div className="flex justify-between gap-8 text-sm mb-2">

                                  <span className="text-gray-500">
                                    Raw Hours
                                  </span>

                                  <span className="font-medium">
                                    {employeeRawHours}
                                  </span>

                                </div>


                                <div className="flex justify-between gap-8 text-sm mb-2">

                                  <span className="text-gray-500">
                                    Automatic Breaks
                                  </span>

                                  <span className="font-medium">
                                    -{employeeBreakHours}
                                  </span>

                                </div>


                                <div className="flex justify-between gap-8 text-sm mb-2">

                                  <span className="text-gray-500">
                                    Counted Hours
                                  </span>

                                  <span className="font-medium">
                                    {employeeCountedHours}
                                  </span>

                                </div>


                                <div className="flex justify-between gap-8 text-sm mb-2">

                                  <span className="text-gray-500">
                                    Regular Hours
                                  </span>

                                  <span className="font-medium">
                                    {employeeRegularHours}
                                  </span>

                                </div>


                                <div className="flex justify-between gap-8 text-sm mb-4">

                                  <span className="text-gray-500">
                                    Overtime Hours
                                  </span>

                                  <span className="font-medium">
                                    {employeeOvertimeHours}
                                  </span>

                                </div>


                                <div className="border-t pt-4">


                                  <div className="flex justify-between gap-8 text-sm mb-2">

                                    <span className="text-gray-500">
                                      Regular Pay
                                    </span>

                                    <span className="font-medium">

                                      {formatCurrency(
                                        employeeTotals.regularPay
                                      )}

                                    </span>

                                  </div>


                                  <div className="flex justify-between gap-8 text-sm mb-3">

                                    <span className="text-gray-500">
                                      Overtime Pay
                                    </span>

                                    <span className="font-medium">

                                      {formatCurrency(
                                        employeeTotals.overtimePay
                                      )}

                                    </span>

                                  </div>


                                  <div className="border-t-2 pt-4 mt-2">

                                    <div className="flex justify-between items-center">

                                      <span className="font-bold text-gray-800">
                                        TOTAL AMOUNT TO PAY
                                      </span>

                                      <span className="text-3xl font-bold text-green-700">

                                        {formatCurrency(
                                          employeeTotals.totalPay
                                        )}

                                      </span>

                                    </div>

                                  </div>

                                </div>

                              </div>

                            </div>

                          </div>

                        )}

                      </div>

                    );

                  }
                )}

              </div>

            )}

        </div>

      </div>

    </div>

  );

};


export default AdminTimeReportManage;